import { useEffect, useMemo, useState } from "react";

import { css } from "@compiled/react";
import { defineMessages, useIntl } from "react-intl";
import { useFilterableGalleryLoader } from "src/uniform/contexts/filterable-gallery-loader-context";

import ArrowRightIcon from "@atlaskit/icon/core/arrow-right";
import CrossIcon from "@atlaskit/icon/core/cross";
import SearchIcon from "@atlaskit/icon/core/search";
import { media } from "@atlaskit/primitives/responsive";
import { token } from "@atlaskit/tokens";

import { wacToken } from "@/libraries/design/foundations";
import { useLocale } from "@/libraries/web-framework/src/localization/use-locale";
import { getLocalizedHref } from "@/libraries/web-framework/src/localization/use-localized-href";

import { isContentfulEnvelope } from "../../../../clients/uniform-client/enhancers/contentful-includes-enhancer";
import { useWAEContextSafe } from "../../../utils/wae-context-provider";
import { GALLERY_SEARCH_EVENT } from "../../filterable-gallery/src/constants";
import {
    coerceToPrimitiveArray,
    resolveFieldPath,
    searchWithFeaturedBoost
} from "../../filterable-gallery/src/utils/filterEngine";
import { dispatchShowAllFilterableGalleryItems } from "../../filterable-gallery-display-limit-override";

import type { SearchConfiguration } from "../../filterable-gallery/src/utils/filterEngine";

const SEARCH_QUERY_PARAM = "term";
const INVERSE_TEXT_COLOR = token("color.text.inverse", "#FFFFFF");

const SUGGESTIONS_LISTBOX_ID = "templates-search-suggestions-listbox";
const ALL_RESULTS_OPTION_ID = "templates-search-all-results-option";
const buildSuggestionOptionId = (index: number) =>
    `templates-search-suggestion-option-${index}`;

export interface TemplatesSearchBarProps {
    readonly heading?: string;
    readonly subheading?: string;
    readonly placeholder?: string;
    readonly items?: unknown;
    readonly suggestionLabelPath?: string;
    readonly suggestionProductPath?: string;
    readonly suggestionHrefPath?: string;
    readonly maxSuggestions?: number;
}

interface Suggestion {
    readonly id: string;
    readonly label: string;
    readonly queryValue: string;
    readonly href?: string;
}

const messages = defineMessages({
    heading: {
        id: "templates-search-bar.heading",
        defaultMessage: "Search all templates below"
    },
    subheading: {
        id: "templates-search-bar.subheading",
        defaultMessage: "Find Atlassian templates in one place."
    },
    placeholder: {
        id: "templates-search-bar.placeholder",
        defaultMessage: "Search templates"
    },
    clearSearch: {
        id: "templates-search-bar.clear-search",
        defaultMessage: "Clear search"
    },
    allSearchResults: {
        id: "templates-search-bar.all-search-results",
        defaultMessage: "All search results"
    }
});

const DEFAULT_PROPS = {
    suggestionLabelPath: "fields.templateNameExternal",
    suggestionProductPath:
        "fields.product.0.fields.productNameReference.fields.productName",
    suggestionHrefPath: "fields.fullUrlSlug",
    maxSuggestions: 6
} as const;

const handleSuggestionKeyDown = ({
    event,
    suggestions,
    activeIndex,
    setActiveIndex,
    setValue,
    submitSearch,
    submitAllResults,
    dismissSuggestions
}: {
    event: React.KeyboardEvent<HTMLInputElement>;
    suggestions: Suggestion[];
    activeIndex: number;
    setActiveIndex: (updater: number | ((current: number) => number)) => void;
    setValue: (value: string) => void;
    submitSearch: (value: string) => void;
    submitAllResults: () => void;
    dismissSuggestions: () => void;
}) => {
    // "All search results" is the final navigable row, past the last suggestion.
    const allResultsIndex = suggestions.length;

    if (event.key === "Escape") {
        event.preventDefault();
        dismissSuggestions();
        return;
    }

    if (event.key === "ArrowDown") {
        event.preventDefault();
        setActiveIndex((current: number) =>
            Math.min(current + 1, allResultsIndex)
        );
        return;
    }

    if (event.key === "ArrowUp") {
        event.preventDefault();
        setActiveIndex((current: number) => Math.max(current - 1, 0));
        return;
    }

    if (event.key === "Enter" && activeIndex >= 0) {
        event.preventDefault();
        if (activeIndex === allResultsIndex) {
            submitAllResults();
            return;
        }
        const suggestion = suggestions[activeIndex];
        if (!suggestion) return;

        if (suggestion.href) {
            window.location.href = suggestion.href;
            return;
        }
        setValue(suggestion.queryValue);
        submitSearch(suggestion.queryValue);
    }
};

/** Suggestions from the gallery's shared items + config (so they match the gallery); falls back to the `items` prop when there's no gallery context. */
const useSuggestions = ({
    rawItems,
    query,
    labelPath,
    productPath,
    hrefPath,
    maxSuggestions,
    formatSuggestionLabel,
    activeLocale
}: {
    rawItems: unknown;
    query: string;
    labelPath: string;
    productPath: string;
    hrefPath: string;
    maxSuggestions: number;
    formatSuggestionLabel: (parts: {
        title: string;
        product: string;
    }) => string;
    activeLocale: string;
}): Suggestion[] => {
    const galleryConfig = useFilterableGalleryLoader()?.galleryConfig ?? null;

    const items = useMemo(
        () => galleryConfig?.items ?? normaliseItems(rawItems),
        [galleryConfig, rawItems]
    );

    return useMemo(
        () =>
            buildSuggestions({
                query,
                items,
                labelPath,
                productPath,
                hrefPath,
                searchConfiguration: galleryConfig?.searchConfiguration,
                featuredItemSlugs: galleryConfig?.featuredItemSlugs ?? [],
                maxSuggestions,
                formatSuggestionLabel,
                activeLocale
            }),
        [
            query,
            items,
            labelPath,
            productPath,
            hrefPath,
            galleryConfig,
            maxSuggestions,
            formatSuggestionLabel,
            activeLocale
        ]
    );
};

const TemplatesSearchBar = ({
    heading,
    subheading,
    placeholder,
    items: rawItems,
    suggestionLabelPath = DEFAULT_PROPS.suggestionLabelPath,
    suggestionProductPath = DEFAULT_PROPS.suggestionProductPath,
    suggestionHrefPath = DEFAULT_PROPS.suggestionHrefPath,
    maxSuggestions = DEFAULT_PROPS.maxSuggestions
}: TemplatesSearchBarProps): JSX.Element => {
    const intl = useIntl();

    // Search bar chrome is localized from the active page locale; the default
    // (English) locale resolves to the original copy. CMS-provided overrides
    // take precedence. See GWP-12213.
    const resolvedHeading = heading ?? intl.formatMessage(messages.heading);
    const resolvedSubheading =
        subheading ?? intl.formatMessage(messages.subheading);
    const resolvedPlaceholder =
        placeholder ?? intl.formatMessage(messages.placeholder);

    return (
        <section css={sectionStyle} data-testid="templates-search-bar">
            <h2
                className="templates-search-bar-heading"
                css={headingStyle}
                style={{ color: INVERSE_TEXT_COLOR }}
            >
                {resolvedHeading}
            </h2>
            {resolvedSubheading && (
                <p
                    className="templates-search-bar-subheading"
                    css={subheadingStyle}
                    style={{ color: INVERSE_TEXT_COLOR }}
                >
                    {resolvedSubheading}
                </p>
            )}
            <TemplatesSearchForm
                placeholder={resolvedPlaceholder}
                rawItems={rawItems}
                suggestionLabelPath={suggestionLabelPath}
                suggestionProductPath={suggestionProductPath}
                suggestionHrefPath={suggestionHrefPath}
                maxSuggestions={maxSuggestions}
            />
        </section>
    );
};

const TemplatesSearchForm = ({
    placeholder,
    rawItems,
    suggestionLabelPath,
    suggestionProductPath,
    suggestionHrefPath,
    maxSuggestions
}: {
    placeholder: string;
    rawItems: unknown;
    suggestionLabelPath: string;
    suggestionProductPath: string;
    suggestionHrefPath: string;
    maxSuggestions: number;
}): JSX.Element => {
    const intl = useIntl();
    const intlLocale = useLocale();
    const activeLocale = useWAEContextSafe()?.locale ?? intlLocale;
    const formatSuggestionLabel = useMemo(
        () =>
            ({ title, product }: { title: string; product: string }): string =>
                intl.formatMessage(
                    {
                        id: "templates-search-bar.suggestion-label",
                        defaultMessage: "{title} - {product} Template"
                    },
                    { title, product }
                ),
        [intl]
    );
    const [value, setValue] = useState("");
    const [isFocused, setIsFocused] = useState(false);
    const [isDismissed, setIsDismissed] = useState(false);
    const [activeIndex, setActiveIndex] = useState(-1);

    const suggestions = useSuggestions({
        rawItems,
        query: value,
        labelPath: suggestionLabelPath,
        productPath: suggestionProductPath,
        hrefPath: suggestionHrefPath,
        maxSuggestions,
        formatSuggestionLabel,
        activeLocale
    });

    const shouldShowSuggestions =
        isFocused &&
        !isDismissed &&
        value.trim().length > 0 &&
        suggestions.length > 0;

    const dismissSuggestions = () => {
        setIsDismissed(true);
        setActiveIndex(-1);
    };

    const activeDescendantId =
        shouldShowSuggestions && activeIndex >= 0
            ? activeIndex >= suggestions.length
                ? ALL_RESULTS_OPTION_ID
                : buildSuggestionOptionId(activeIndex)
            : undefined;

    useEffect(() => {
        if (typeof window === "undefined") return;

        const hydrateFromUrl = () => {
            const params = new URLSearchParams(window.location.search);
            setValue(params.get(SEARCH_QUERY_PARAM) ?? "");
        };

        hydrateFromUrl();
        window.addEventListener("popstate", hydrateFromUrl);
        return () => window.removeEventListener("popstate", hydrateFromUrl);
    }, []);

    const submitSearch = (nextValue = value) => {
        writeSearchQuery(nextValue);
        setIsFocused(false);
        setActiveIndex(-1);
    };

    const clearSearch = () => {
        setValue("");
        writeSearchQuery("");
        setActiveIndex(-1);
    };

    const submitAllSearchResults = () => {
        submitSearch();
        dispatchShowAllFilterableGalleryItems(value);
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (!shouldShowSuggestions) return;

        handleSuggestionKeyDown({
            event,
            suggestions,
            activeIndex,
            setActiveIndex,
            setValue,
            submitSearch,
            submitAllResults: submitAllSearchResults,
            dismissSuggestions
        });
    };

    return (
        <form
            css={searchFormStyle}
            role="search"
            onBlur={(event) => {
                if (!event.currentTarget.contains(event.relatedTarget)) {
                    setIsFocused(false);
                }
            }}
            onFocus={() => {
                setIsFocused(true);
                setIsDismissed(false);
            }}
            onSubmit={(event) => {
                event.preventDefault();
                submitAllSearchResults();
            }}
        >
            <div css={inputShellStyle}>
                <span css={searchIconStyle} aria-hidden="true">
                    <SearchIcon label="" size="medium" />
                </span>
                <input
                    css={inputStyle}
                    aria-label={placeholder}
                    placeholder={placeholder}
                    value={value}
                    role="combobox"
                    aria-autocomplete="list"
                    aria-expanded={shouldShowSuggestions}
                    aria-controls={SUGGESTIONS_LISTBOX_ID}
                    aria-activedescendant={activeDescendantId}
                    onChange={(event) => {
                        setValue(event.target.value);
                        setIsFocused(true);
                        setIsDismissed(false);
                        setActiveIndex(-1);
                    }}
                    onKeyDown={handleKeyDown}
                />
                {value && (
                    <button
                        type="button"
                        css={clearButtonStyle}
                        aria-label={intl.formatMessage(messages.clearSearch)}
                        onClick={clearSearch}
                    >
                        <CrossIcon label="" size="medium" />
                    </button>
                )}
            </div>

            {shouldShowSuggestions && (
                <SuggestionsPanel
                    suggestions={suggestions}
                    activeIndex={activeIndex}
                    query={value}
                    setActiveIndex={setActiveIndex}
                    onSelect={(suggestion) => {
                        setValue(suggestion.queryValue);
                        submitSearch(suggestion.queryValue);
                    }}
                    onAllResults={submitAllSearchResults}
                    allResultsLabel={intl.formatMessage(
                        messages.allSearchResults
                    )}
                />
            )}
        </form>
    );
};

const normaliseItems = (rawItems: unknown): unknown[] => {
    if (Array.isArray(rawItems)) return rawItems;
    if (isContentfulEnvelope(rawItems)) return rawItems.items;
    return [];
};

const resolveStrings = (item: unknown, path: string): string[] =>
    coerceToPrimitiveArray(resolveFieldPath(item, path));

const resolveString = (item: unknown, path: string): string | undefined =>
    resolveStrings(item, path)[0];

/** Fallback config (when the gallery shares none) ranking title above product, over the search bar's own display paths. */
const buildRankingConfig = (
    labelPath: string,
    productPath: string
): SearchConfiguration => ({
    keys: [
        { name: labelPath, weight: 1 },
        ...(productPath ? [{ name: productPath, weight: 0.7 }] : [])
    ]
});

/** Builds the dropdown: the raw query first, then items ranked by the same search the gallery uses, deduped by display label. */
const buildSuggestions = ({
    query,
    items,
    labelPath,
    productPath,
    hrefPath,
    searchConfiguration,
    featuredItemSlugs,
    maxSuggestions,
    formatSuggestionLabel,
    activeLocale
}: {
    query: string;
    items: unknown[];
    labelPath: string;
    productPath: string;
    hrefPath: string;
    searchConfiguration?: SearchConfiguration;
    featuredItemSlugs: string[];
    maxSuggestions: number;
    formatSuggestionLabel: (parts: {
        title: string;
        product: string;
    }) => string;
    activeLocale: string;
}): Suggestion[] => {
    const trimmedQuery = query.trim();
    if (!trimmedQuery) return [];

    const lowerQuery = trimmedQuery.toLowerCase();
    const suggestionLimit = Math.max(maxSuggestions, 0);
    if (suggestionLimit === 0) return [];

    const suggestions: Suggestion[] = [
        {
            id: `query-${lowerQuery}`,
            label: trimmedQuery,
            queryValue: trimmedQuery
        }
    ];
    if (suggestionLimit === 1) return suggestions;

    const rankedItems = searchWithFeaturedBoost(
        items,
        trimmedQuery,
        searchConfiguration ?? buildRankingConfig(labelPath, productPath),
        featuredItemSlugs
    );

    const seen = new Set([lowerQuery]);
    for (const item of rankedItems) {
        const title = resolveString(item, labelPath);
        if (!title) continue;

        const product = resolveString(item, productPath);
        const label = product
            ? formatSuggestionLabel({ title, product })
            : title;
        const key = label.toLowerCase();
        if (seen.has(key)) continue;
        seen.add(key);

        const href = resolveString(item, hrefPath);
        suggestions.push({
            id: key,
            label,
            queryValue: title,
            href: href
                ? getLocalizedHref({ href, locale: activeLocale })
                : undefined
        });

        if (suggestions.length >= suggestionLimit) break;
    }

    return suggestions;
};

const writeSearchQuery = (query: string) => {
    if (typeof window === "undefined") return;

    const params = new URLSearchParams(window.location.search);
    const trimmedQuery = query.trim();
    if (trimmedQuery) {
        params.set(SEARCH_QUERY_PARAM, trimmedQuery);
    } else {
        params.delete(SEARCH_QUERY_PARAM);
    }

    const nextSearch = params.toString();
    const nextUrl = `${window.location.pathname}${
        nextSearch ? `?${nextSearch}` : ""
    }${window.location.hash}`;

    if (
        nextUrl !==
        `${window.location.pathname}${window.location.search}${window.location.hash}`
    ) {
        window.history.pushState({}, "", nextUrl);
        window.dispatchEvent(new Event("popstate"));
    }
    window.dispatchEvent(
        new CustomEvent(GALLERY_SEARCH_EVENT, {
            detail: { query: trimmedQuery }
        })
    );
};

const SuggestionsPanel = ({
    suggestions,
    activeIndex,
    query,
    setActiveIndex,
    onSelect,
    onAllResults,
    allResultsLabel
}: {
    suggestions: Suggestion[];
    activeIndex: number;
    query: string;
    setActiveIndex: (index: number) => void;
    onSelect: (suggestion: Suggestion) => void;
    onAllResults: () => void;
    allResultsLabel: string;
}) => (
    <div css={suggestionsPanelStyle} data-testid="templates-search-suggestions">
        <ul
            id={SUGGESTIONS_LISTBOX_ID}
            role="listbox"
            aria-label={allResultsLabel}
            css={suggestionsListStyle}
        >
            {suggestions.map((suggestion, index) => {
                const isActive = index === activeIndex;
                const sharedCss = [
                    suggestionButtonStyle,
                    isActive ? activeSuggestionStyle : null
                ];
                const label = (
                    <HighlightedText text={suggestion.label} query={query} />
                );
                // role="presentation" <li> keeps valid <ul> markup while the
                // option (link, or button for the typed-query row) stays a
                // direct listbox child.
                return (
                    <li key={suggestion.id} role="presentation">
                        {suggestion.href ? (
                            <a
                                id={buildSuggestionOptionId(index)}
                                role="option"
                                aria-selected={isActive}
                                aria-label={suggestion.label}
                                css={sharedCss}
                                href={suggestion.href}
                                tabIndex={-1}
                                onMouseDown={(event) => event.preventDefault()}
                                onMouseEnter={() => setActiveIndex(index)}
                                onFocus={() => setActiveIndex(index)}
                            >
                                {label}
                            </a>
                        ) : (
                            <button
                                type="button"
                                id={buildSuggestionOptionId(index)}
                                role="option"
                                aria-selected={isActive}
                                aria-label={suggestion.label}
                                css={sharedCss}
                                tabIndex={-1}
                                onMouseDown={(event) => event.preventDefault()}
                                onMouseEnter={() => setActiveIndex(index)}
                                onFocus={() => setActiveIndex(index)}
                                onClick={() => onSelect(suggestion)}
                            >
                                {label}
                            </button>
                        )}
                    </li>
                );
            })}
            <li role="presentation">
                <button
                    type="button"
                    id={ALL_RESULTS_OPTION_ID}
                    role="option"
                    aria-selected={activeIndex === suggestions.length}
                    css={[
                        allResultsButtonStyle,
                        activeIndex === suggestions.length
                            ? activeSuggestionStyle
                            : null
                    ]}
                    tabIndex={-1}
                    onMouseDown={(event) => event.preventDefault()}
                    onMouseEnter={() => setActiveIndex(suggestions.length)}
                    onFocus={() => setActiveIndex(suggestions.length)}
                    onClick={onAllResults}
                >
                    {allResultsLabel}
                    <ArrowRightIcon label="" size="small" />
                </button>
            </li>
        </ul>
    </div>
);

const HighlightedText = ({
    text,
    query
}: {
    readonly text: string;
    readonly query: string;
}) => {
    const trimmedQuery = query.trim();
    const index = text.toLowerCase().indexOf(trimmedQuery.toLowerCase());

    if (!trimmedQuery || index < 0) return <>{text}</>;

    return (
        <>
            {text.slice(0, index)}
            <strong>{text.slice(index, index + trimmedQuery.length)}</strong>
            {text.slice(index + trimmedQuery.length)}
        </>
    );
};

const sectionStyle = css({
    backgroundColor: wacToken("color.primary.black"),
    color: wacToken("color.neutral.light0"),
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
    padding: "80px 24px",
    overflow: "visible",
    [media.below.sm]: {
        padding: "64px 16px"
    }
});

const headingStyle = css({
    color: `${INVERSE_TEXT_COLOR} !important`,
    margin: 0,
    fontSize: "2.5rem",
    lineHeight: "1.15",
    fontWeight: 700,
    [media.below.sm]: {
        fontSize: "2rem"
    }
});

const subheadingStyle = css({
    color: `${INVERSE_TEXT_COLOR} !important`,
    margin: `${token("space.200")} 0 0`,
    fontSize: "1.25rem",
    lineHeight: "1.4"
});

const searchFormStyle = css({
    position: "relative",
    width: "100%",
    maxWidth: "768px",
    marginBlockStart: "44px"
});

const inputShellStyle = css({
    width: "100%",
    height: "40px",
    borderRadius: "20px",
    backgroundColor: wacToken("color.neutral.light0"),
    display: "flex",
    alignItems: "center",
    padding: "0 16px 0 20px",
    color: wacToken("color.primary.black")
});

const searchIconStyle = css({
    color: token("color.icon.subtle"),
    display: "flex",
    alignItems: "center",
    flex: "0 0 auto",
    marginInlineEnd: token("space.150")
});

const inputStyle = css({
    border: 0,
    outline: 0,
    flex: "1 1 auto",
    minWidth: 0,
    backgroundColor: "transparent",
    color: wacToken("color.primary.black"),
    fontSize: "1rem",
    lineHeight: "1.5",
    fontFamily: "inherit"
});

const clearButtonStyle = css({
    background: "transparent",
    border: 0,
    color: wacToken("color.primary.black"),
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flex: "0 0 auto",
    padding: token("space.050")
});

const suggestionsPanelStyle = css({
    position: "absolute",
    zIndex: 5,
    insetBlockStart: "56px",
    insetInlineStart: 0,
    width: "100%",
    backgroundColor: wacToken("color.neutral.light0"),
    color: wacToken("color.primary.black"),
    borderRadius: "20px",
    padding: "26px 28px 22px",
    textAlign: "left",
    border: `1px solid ${token("color.border.selected")}`,
    boxShadow: token("elevation.shadow.overlay")
});

const suggestionsListStyle = css({
    listStyle: "none",
    margin: 0,
    padding: 0,
    display: "flex",
    flexDirection: "column",
    gap: token("space.100")
});

const suggestionButtonStyle = css({
    width: "100%",
    border: 0,
    backgroundColor: "transparent",
    color: wacToken("color.primary.black"),
    cursor: "pointer",
    display: "block",
    textAlign: "left",
    padding: `${token("space.050")} ${token("space.100")}`,
    minHeight: "32px",
    fontSize: "1rem",
    lineHeight: "1.5",
    fontFamily: "inherit"
});

const activeSuggestionStyle = css({
    backgroundColor: wacToken("color.neutral.light200")
});

const allResultsButtonStyle = css({
    "border": 0,
    "backgroundColor": "transparent",
    "color": token("color.link"),
    "cursor": "pointer",
    "display": "inline-flex",
    "alignItems": "center",
    "gap": token("space.050"),
    "marginBlockStart": token("space.200"),
    "padding": `${token("space.050")} ${token("space.100")}`,
    "fontSize": "1rem",
    "fontWeight": 600,
    "fontFamily": "inherit",
    "&:hover, &:focus-visible": {
        backgroundColor: wacToken("color.neutral.light200"),
        outline: 0
    }
});

export default TemplatesSearchBar;
